import { test, expect } from '@playwright/test';
import { expect as sdsExpect } from '../playwright-util/custom-matchers';

const textfieldIds = [
	'textfield-name',
	'textfield-label',
	'textfield-placeholder',
	'textfield-helptext',
	'textfield-disabled',
	'textfield-readonly',
	'textfield-required',
	'textfield-error',
	'textfield-icon',
	'textfield-autocomplete-off',
	'textfield-autocomplete-on',
	'textfield-pattern',
	'textfield-min',
	'textfield-max',
];

const pageUrl = 'iframe.html?viewMode=docs&id=test-textfield--docs';

test.describe('textfield-component', () => {
	test.beforeEach(async ({ page }) => {
		await page.goto(pageUrl);
	});

	test('visibility', async ({ page }) => {
		for (const id of textfieldIds) {
			await expect(page.locator(`#${id}`)).toBeVisible();
		}
	});

	test('design-implementation', async ({ page }) => {
		await page.reload();

		for (const id of textfieldIds) {
			// Default state
			await expect(page.locator(`#${id}`)).toHaveScreenshot(`${id}.png`);
			// Hover state
			if (!id.includes('disabled')) {
				await page.locator(`#${id}`).hover();
				await expect(page.locator(`#${id}`)).toHaveScreenshot(`${id}-hover.png`);
			}
			// Focus state
			await page.evaluate((id) => {
				const element = document.getElementById(id);
				if (element) {
					const parentDiv = document.createElement('div');
					parentDiv.style.padding = '10px';
					parentDiv.style.backgroundColor = 'white';
					element.parentNode?.insertBefore(parentDiv, element);
					parentDiv.appendChild(element);
				}
			}, id);
			await page.locator(`#${id}`).focus();
			const parentDiv = await page.locator(`#${id}`).locator('xpath=..');
			await expect(parentDiv).toHaveScreenshot(`${id}-focus.png`);
		}
	});

	test('form-submission', async ({ page }) => {
		let requestUrl: URL | undefined;
		page.on('request', (request) => {
			if (request.url().includes('iframe.html?name=John')) {
				requestUrl = new URL(request.url());
			}
		});

		await page.locator('#textfield-name').pressSequentially('John');
		await page.locator('#submit-button').click();
		await expect(requestUrl?.searchParams.get('name')).toBe('John');
	});

	test('form-reset', async ({ page }) => {
		await page.locator('#textfield-name').pressSequentially('John');
		await page.locator('#reset-button').click();
		const value = await page.locator('#textfield-name').getAttribute('value');
		expect(value).toBeNull();
	});

	test('minlength', async ({ page }) => {
		const textfield = page.locator('#textfield-min');
		await expect(textfield).toHaveAttribute('minlength', '4');
		const nestedInputHasMinlength = await textfield.evaluate((el: HTMLInputElement) => el.hasAttribute('minlength'));
		expect(nestedInputHasMinlength).toBe(true);
	});

	test('maxlength', async ({ page }) => {
		const textfield = page.locator('#textfield-max');

		await textfield.pressSequentially('abcdef');
		await sdsExpect(textfield).toHaveValue('abcd');
	});

	test('pattern', async ({ page }) => {
		const textfield = page.locator('#textfield-pattern');

		// Non-digits → invalid
		await textfield.pressSequentially('abcd');
		let isValid = await textfield.evaluate((el: HTMLInputElement) => el.checkValidity());
		expect(isValid).toBe(false);

		// Too few digits → invalid
		await page.goto(pageUrl);
		await textfield.pressSequentially('123');
		isValid = await textfield.evaluate((el: HTMLInputElement) => el.checkValidity());
		expect(isValid).toBe(false);

		// Exactly 4 digits → valid
		await page.goto(pageUrl);
		await textfield.pressSequentially('1234');
		isValid = await textfield.evaluate((el: HTMLInputElement) => el.checkValidity());
		expect(isValid).toBe(true);

		// Too many digits → invalid
		await page.goto(pageUrl);
		await textfield.pressSequentially('12345');
		isValid = await textfield.evaluate((el: HTMLInputElement) => el.checkValidity());
		expect(isValid).toBe(false);
	});

	test('required', async ({ page }) => {
		const textfield = page.locator('#textfield-required');

		// No value → invalid
		let isValid = await textfield.evaluate((el: HTMLInputElement) => el.checkValidity());
		expect(isValid).toBe(false);

		// Has value → valid
		await page.goto(pageUrl);
		await textfield.pressSequentially('abc');
		isValid = await textfield.evaluate((el: HTMLInputElement) => el.checkValidity());
		expect(isValid).toBe(true);
	});

	test('readonly', async ({ page }) => {
		const textfield = page.locator('#textfield-readonly');
		await textfield.pressSequentially('abc');
		await sdsExpect(textfield).toHaveValue('Kan ikke endres');
	});

	test('autocomplete on/off', async ({ page }) => {
		const textfields = [
			{ id: 'textfield-autocomplete-off', expected: 'off' },
			{ id: 'textfield-autocomplete-on', expected: 'on' },
		];

		for (const { id, expected } of textfields) {
			const host = page.locator(`#${id}`);

			await expect(host).toHaveAttribute('autocomplete', expected);
			const shadowDomInput = await host.evaluate((el: HTMLElement) => {
				const input = el.shadowRoot?.querySelector('input') as HTMLInputElement | null;
				return input?.autocomplete ?? null;
			});
			expect(shadowDomInput).toBe(expected);
		}
	});
});
